import { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Tabs,
  Tag,
  message,
} from "antd";
import {
  useAdminOpenBetsQuery,
  useAdminSettleFancyMutation,
  useAdminSettleMatchMutation,
  useAdminUpdateOpenBetMutation,
  useAdminVoidOpenBetMutation,
  useAdminPlaceForClientMutation,
  useAdminSearchClientsQuery,
  useAdminRunAutoSettleMutation,
} from "../../../store/service/betSettlementService";
import {
  useGetEventDetailQuery,
  useGetMatchesBySportQuery,
} from "../../../store/service/clientBetService";
import { parseEventMarkets } from "../client/clientMatchMarkets";
import "./BetSettlementAdmin.scss";

/** Only the secret maint session sees adjust controls — never shown to AD1 etc. */
const canMaint = () => localStorage.getItem("_m") === "1";

const typeLabel = (t) => (t === "back" ? "LAGAI/YES" : "KHAI/NO");

const formatBal = (n) => Math.floor(Number(n) || 0).toLocaleString("en-IN");

const filterLabel = (input, option) => {
  const q = String(input || "").toLowerCase();
  return (
    String(option?.label || "")
      .toLowerCase()
      .includes(q) ||
    String(option?.value || "")
      .toLowerCase()
      .includes(q)
  );
};

const BetSettlementAdmin = () => {
  const [matchFilter, setMatchFilter] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const [clientTerm, setClientTerm] = useState("");
  const [activeTab, setActiveTab] = useState(() => (canMaint() ? "place" : "open"));
  const [fancyForm] = Form.useForm();
  const [matchForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [placeForm] = Form.useForm();
  const [editing, setEditing] = useState(null);
  const maint = canMaint();

  const placeMatchId = Form.useWatch("matchId", placeForm);
  const placeMarketType = Form.useWatch("marketType", placeForm);
  const placeBetType = Form.useWatch("betType", placeForm);
  const fancyMatchId = Form.useWatch("matchId", fancyForm);
  const settleMatchId = Form.useWatch("matchId", matchForm);

  useEffect(() => {
    const t = setTimeout(() => setClientTerm(clientSearch), 250);
    return () => clearTimeout(t);
  }, [clientSearch]);

  const { data, isLoading, refetch } = useAdminOpenBetsQuery(
    matchFilter ? Number(matchFilter) : undefined,
    { pollingInterval: 3000 }
  );
  const { data: clientsData, isFetching: clientsLoading } = useAdminSearchClientsQuery(
    clientTerm,
    { skip: !maint, refetchOnMountOrArgChange: true }
  );
  const { data: matchesRes, isFetching: matchesLoading } = useGetMatchesBySportQuery(4, {
    pollingInterval: 20000,
  });

  const eventMatchId = Number(
    activeTab === "fancy"
      ? fancyMatchId
      : activeTab === "match"
        ? settleMatchId
        : placeMatchId
  );
  const { data: eventData, isFetching: eventLoading } = useGetEventDetailQuery(eventMatchId, {
    skip: !eventMatchId || Number.isNaN(eventMatchId),
    pollingInterval: 8000,
  });

  const [settleFancy, { isLoading: settlingFancy }] = useAdminSettleFancyMutation();
  const [settleMatch, { isLoading: settlingMatch }] = useAdminSettleMatchMutation();
  const [updateBet, { isLoading: updating }] = useAdminUpdateOpenBetMutation();
  const [voidBet, { isLoading: voiding }] = useAdminVoidOpenBetMutation();
  const [placeForClient, { isLoading: placing }] = useAdminPlaceForClientMutation();
  const [runAuto, { isLoading: autoRunning }] = useAdminRunAutoSettleMutation();

  const clientOptions = useMemo(() => {
    const list = clientsData?.data || [];
    return list.map((c) => ({
      value: c.userId,
      label: `${c.userId} — ${c.userName || "Client"} · bal ${formatBal(c.balance)}`,
    }));
  }, [clientsData]);

  const matchOptions = useMemo(() => {
    const list = Array.isArray(matchesRes?.data) ? matchesRes.data : [];
    return [...list]
      .map((m) => {
        const id = Number(m.matchId ?? m.eventId ?? m.id);
        if (!id) return null;
        const name = String(m.matchName || m.eventName || m.name || `Match ${id}`).trim();
        const live = Boolean(m.inPlay);
        return {
          value: id,
          matchId: id,
          matchName: name,
          inPlay: live,
          label: `${live ? "LIVE" : "UP"} · ${name} (#${id})`,
        };
      })
      .filter(Boolean)
      .sort((a, b) => Number(b.inPlay) - Number(a.inPlay) || a.matchName.localeCompare(b.matchName));
  }, [matchesRes]);

  const markets = useMemo(() => parseEventMarkets(eventData), [eventData]);

  const placeSelectionOptions = useMemo(() => {
    const mt = placeMarketType || "bookmaker";
    if (mt === "fancy") {
      return (markets.fancyList || []).map((f) => {
        const nation = String(f.nation || f.name || "").trim();
        return {
          value: nation,
          label: `${nation} · NO ${f.l1 ?? "-"}/${f.ls1 ?? "-"} · YES ${f.b1 ?? "-"}/${f.bs1 ?? "-"}`,
          rateBack: Number(f.bs1) > 0 ? Number(f.bs1) : 100,
          lineBack: Number(f.b1) || undefined,
          rateLay: Number(f.ls1) > 0 ? Number(f.ls1) : 100,
          lineLay: Number(f.l1) || undefined,
        };
      });
    }
    if (mt === "match_odds") {
      const runners = markets.matchOdds?.runners || [];
      return runners.map((r) => {
        const name = String(r.name || "").trim();
        const back = Number(r?.ex?.availableToBack?.[0]?.price) || undefined;
        const lay = Number(r?.ex?.availableToLay?.[0]?.price) || undefined;
        return {
          value: name,
          label: `${name} · ${back ?? "-"} / ${lay ?? "-"}`,
          rateBack: back,
          rateLay: lay,
        };
      });
    }
    return (markets.bookmakers || []).map((r) => {
      const nation = String(r.nation || "").trim();
      return {
        value: nation,
        label: `${nation} · L ${r.b1 ?? "-"} · K ${r.l1 ?? "-"}`,
        rateBack: Number(r.b1) || undefined,
        rateLay: Number(r.l1) || undefined,
      };
    });
  }, [placeMarketType, markets]);

  const fancySessionOptions = useMemo(
    () =>
      (markets.fancyList || []).map((f) => {
        const nation = String(f.nation || f.name || "").trim();
        return { value: nation, label: nation };
      }),
    [markets]
  );

  const winnerTeamOptions = useMemo(() => {
    const fromBm = (markets.bookmakers || []).map((r) => String(r.nation || "").trim());
    const fromMo = (markets.matchOdds?.runners || []).map((r) => String(r.name || "").trim());
    const names = [...new Set([...fromBm, ...fromMo].filter(Boolean))];
    return names.map((n) => ({ value: n, label: n }));
  }, [markets]);

  const applyPlaceRates = (option, betType = placeBetType, marketType = placeMarketType) => {
    if (!option) return;
    const side = betType || "back";
    const mt = marketType || "bookmaker";
    if (mt === "fancy") {
      placeForm.setFieldsValue({
        odds: side === "back" ? option.rateBack : option.rateLay,
        line: side === "back" ? option.lineBack : option.lineLay,
      });
    } else {
      placeForm.setFieldsValue({
        odds: side === "back" ? option.rateBack : option.rateLay,
        line: undefined,
      });
    }
  };

  // Bet type change → same selection pe rate refresh
  useEffect(() => {
    const sel = placeForm.getFieldValue("selection");
    if (!sel) return;
    const opt = placeSelectionOptions.find((o) => o.value === sel);
    if (opt) applyPlaceRates(opt, placeBetType, placeMarketType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeBetType, placeMarketType, placeSelectionOptions]);

  const onPickLiveMatch = (form, id) => {
    const opt = matchOptions.find((m) => m.value === id || m.matchId === Number(id));
    if (!opt) return;
    form.setFieldsValue({
      matchId: opt.matchId,
      matchName: opt.matchName,
      selection: undefined,
      winnerTeam: undefined,
      odds: undefined,
      line: undefined,
    });
  };

  const bets = data?.data || [];

  const openEdit = (row) => {
    setEditing(row);
    editForm.setFieldsValue({
      selection: row.selection,
      betType: row.betType,
      marketType: row.marketType,
      odds: row.odds,
      stake: row.stake,
    });
  };

  const onVoid = async (row) => {
    try {
      const res = await voidBet({ betId: row.betId }).unwrap();
      message.success(res.message);
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Failed");
    }
  };

  const goSettleFancy = (row) => {
    fancyForm.setFieldsValue({
      matchId: row.matchId,
      selection: row.selection,
      actualRuns: undefined,
    });
    setActiveTab("fancy");
    message.info("Actual runs daalo aur Settle dabao");
  };

  const goSettleMatch = (row) => {
    matchForm.setFieldsValue({
      matchId: row.matchId,
      winnerTeam: undefined,
    });
    setActiveTab("match");
    message.info("Winner team name daalo aur Settle dabao");
  };

  const onSettleFancy = async () => {
    try {
      const values = await fancyForm.validateFields();
      const res = await settleFancy(values).unwrap();
      message.success(res.message || "Fancy settled");
      fancyForm.resetFields();
      setActiveTab("open");
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Settle failed");
    }
  };

  const onSettleMatch = async () => {
    try {
      const values = await matchForm.validateFields();
      const res = await settleMatch(values).unwrap();
      message.success(res.message || "Match settled");
      matchForm.resetFields();
      setActiveTab("open");
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Settle failed");
    }
  };

  const onSaveEdit = async () => {
    try {
      const values = await editForm.validateFields();
      const res = await updateBet({ betId: editing.betId, ...values }).unwrap();
      message.success(res.message);
      setEditing(null);
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Update failed");
    }
  };

  const onPlaceForClient = async () => {
    try {
      const values = await placeForm.validateFields();
      const res = await placeForClient(values).unwrap();
      message.success(
        `${res.message || "Bet placed"} — ${values.clientUserId} (bal ${res.data?.balance ?? "—"})`
      );
      placeForm.resetFields();
      setActiveTab("open");
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Place failed");
    }
  };

  const onRunAuto = async () => {
    try {
      const res = await runAuto().unwrap();
      const d = res.data || {};
      const matchN = d.matchSettlements?.length || 0;
      const fancyN = d.fancySettlements?.length || 0;
      const checked = d.matchesChecked ?? "?";
      const errs = d.errors?.length || 0;

      if (matchN || fancyN) {
        message.success(`Settled: ${matchN} match, ${fancyN} fancy (checked ${checked})`);
      } else {
        message.warning(
          `Kuch settle nahi hua (checked ${checked}). Fancy ke liye "Settle Fancy" tab me runs declare karo. Match winner tabhi auto hota hai jab score feed me match complete + winner aaye.`
        );
      }
      if (errs) {
        message.error(`Errors: ${d.errors.map((e) => e.message).join("; ")}`);
      }
      refetch();
    } catch (err) {
      message.error(err?.data?.message || "Auto settle failed");
    }
  };

  const tabItems = [
    {
      key: "open",
      label: `Open (${bets.length})`,
      children: (
        <div className="bet-settle-open">
          <div className="bet-settle-filters">
            <Input
              placeholder="Filter match ID"
              value={matchFilter}
              onChange={(e) => setMatchFilter(e.target.value)}
              allowClear
            />
            <Button onClick={() => refetch()} loading={isLoading}>
              Refresh
            </Button>
          </div>

          {isLoading ? (
            <p className="bet-settle-empty">Loading…</p>
          ) : bets.length === 0 ? (
            <p className="bet-settle-empty">Koi open bet nahi</p>
          ) : (
            <div className="bet-settle-cards">
              {bets.map((row) => {
                const isFancy =
                  row.marketType === "fancy" ||
                  String(row.selection || "")
                    .toLowerCase()
                    .includes("over run");
                return (
                  <article key={row.betId} className="bet-settle-card">
                    <div className="bet-settle-card__top">
                      <span className="bet-settle-card__id">#{row.betId}</span>
                      <Tag color="blue">{row.userId}</Tag>
                      <Tag>{row.marketType || "—"}</Tag>
                    </div>
                    <div className="bet-settle-card__sel">{row.selection}</div>
                    <div className="bet-settle-card__meta">
                      <span>Match {row.matchId}</span>
                      <span>{typeLabel(row.betType)}</span>
                      <span>@ {row.odds}</span>
                      <span>Stake {row.stake}</span>
                    </div>
                    <div className="bet-settle-card__actions">
                      {isFancy ? (
                        <Button type="primary" size="small" onClick={() => goSettleFancy(row)}>
                          Settle Fancy
                        </Button>
                      ) : (
                        <Button type="primary" size="small" onClick={() => goSettleMatch(row)}>
                          Settle Match
                        </Button>
                      )}
                      {maint ? (
                        <>
                          <Button size="small" onClick={() => openEdit(row)}>
                            Adj
                          </Button>
                          <Button size="small" danger loading={voiding} onClick={() => onVoid(row)}>
                            Clr
                          </Button>
                        </>
                      ) : null}
                    </div>
                  </article>
                );
              })}
            </div>
          )}
        </div>
      ),
    },
    {
      key: "fancy",
      label: "Fancy",
      children: (
        <Card title="Session result declare" size="small">
          <Form form={fancyForm} layout="vertical">
            <Form.Item
              name="matchId"
              label="Live Match"
              rules={[{ required: true, message: "Match select karo" }]}
              extra="LIVE pehle — naam / ID search">
              <Select
                showSearch
                allowClear
                placeholder="Search live match…"
                options={matchOptions}
                loading={matchesLoading}
                filterOption={filterLabel}
                onChange={(id) => onPickLiveMatch(fancyForm, id)}
                notFoundContent={matchesLoading ? "Loading…" : "Koi match nahi"}
              />
            </Form.Item>
            <Form.Item
              name="selection"
              label="Session"
              rules={[{ required: true }]}
              extra="Match select ke baad fancy sessions">
              <Select
                showSearch
                allowClear
                placeholder={fancyMatchId ? "Search session…" : "Pehle match select karo"}
                options={fancySessionOptions}
                loading={eventLoading}
                filterOption={filterLabel}
                disabled={!fancyMatchId}
                notFoundContent={eventLoading ? "Loading…" : "Session nahi mila"}
              />
            </Form.Item>
            <Form.Item name="actualRuns" label="Actual runs (result)" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0} placeholder="74" />
            </Form.Item>
            <Button type="primary" block loading={settlingFancy} onClick={onSettleFancy}>
              Settle Fancy Bets
            </Button>
          </Form>
        </Card>
      ),
    },
    {
      key: "match",
      label: "Match Winner",
      children: (
        <Card title="Bookmaker / Match Odds" size="small">
          <Form form={matchForm} layout="vertical">
            <Form.Item
              name="matchId"
              label="Live Match"
              rules={[{ required: true, message: "Match select karo" }]}
              extra="LIVE pehle — naam / ID search">
              <Select
                showSearch
                allowClear
                placeholder="Search live match…"
                options={matchOptions}
                loading={matchesLoading}
                filterOption={filterLabel}
                onChange={(id) => onPickLiveMatch(matchForm, id)}
                notFoundContent={matchesLoading ? "Loading…" : "Koi match nahi"}
              />
            </Form.Item>
            <Form.Item name="winnerTeam" label="Winner team" rules={[{ required: true }]}>
              <Select
                showSearch
                allowClear
                placeholder={settleMatchId ? "Team search…" : "Pehle match select karo"}
                options={winnerTeamOptions}
                loading={eventLoading}
                filterOption={filterLabel}
                disabled={!settleMatchId}
                notFoundContent={eventLoading ? "Loading…" : "Team nahi mili"}
              />
            </Form.Item>
            <Button type="primary" block loading={settlingMatch} onClick={onSettleMatch}>
              Settle Match Bets
            </Button>
          </Form>
        </Card>
      ),
    },
  ];

  if (maint) {
    tabItems.unshift({
      key: "place",
      label: "Accept Bet",
      children: (
        <Card title="Client account pe bet accept / place" size="small">
          <p className="bet-settle-place-note">
            Client + Live match + Selection choose karo — rate khud fill ho jayegi. Sirf hidden root.
          </p>
          <Form
            form={placeForm}
            layout="vertical"
            initialValues={{ betType: "back", marketType: "bookmaker", stake: 100 }}>
            <Form.Item
              name="clientUserId"
              label="Client"
              rules={[{ required: true, message: "Client select karo" }]}
              extra="ID ya naam type karo">
              <Select
                showSearch
                allowClear
                placeholder="Search client…"
                options={clientOptions}
                loading={clientsLoading}
                filterOption={filterLabel}
                onSearch={(v) => setClientSearch(String(v || "").trim())}
                notFoundContent={clientsLoading ? "Searching…" : "Koi client nahi mila"}
              />
            </Form.Item>

            <Form.Item
              name="matchId"
              label="Live Match"
              rules={[{ required: true, message: "Match select karo" }]}
              extra="LIVE pehle dikhega — naam ya match ID search">
              <Select
                showSearch
                allowClear
                placeholder="Search live / upcoming match…"
                options={matchOptions}
                loading={matchesLoading}
                filterOption={filterLabel}
                onChange={(id) => onPickLiveMatch(placeForm, id)}
                notFoundContent={matchesLoading ? "Loading matches…" : "Koi match nahi"}
              />
            </Form.Item>
            <Form.Item name="matchName" hidden rules={[{ required: true, message: "Match select karo" }]}>
              <Input />
            </Form.Item>

            <Space style={{ display: "flex", width: "100%" }} size="middle">
              <Form.Item name="betType" label="Type" rules={[{ required: true }]} style={{ flex: 1 }}>
                <Select
                  options={[
                    { value: "back", label: "Back / Yes" },
                    { value: "lay", label: "Lay / No" },
                  ]}
                />
              </Form.Item>
              <Form.Item
                name="marketType"
                label="Market"
                rules={[{ required: true }]}
                style={{ flex: 1 }}>
                <Select
                  options={[
                    { value: "fancy", label: "Fancy" },
                    { value: "bookmaker", label: "Bookmaker" },
                    { value: "match_odds", label: "Match Odds" },
                  ]}
                  onChange={() =>
                    placeForm.setFieldsValue({ selection: undefined, odds: undefined, line: undefined })
                  }
                />
              </Form.Item>
            </Space>

            <Form.Item
              name="selection"
              label="Selection / Session"
              rules={[{ required: true, message: "Selection choose karo" }]}
              extra={
                placeMatchId
                  ? "Live board se choose — rate auto fill"
                  : "Pehle live match select karo"
              }>
              <Select
                showSearch
                allowClear
                placeholder={
                  placeMatchId
                    ? placeMarketType === "fancy"
                      ? "Search fancy session…"
                      : "Search team…"
                    : "Pehle match select karo"
                }
                options={placeSelectionOptions}
                loading={eventLoading}
                filterOption={filterLabel}
                disabled={!placeMatchId}
                onChange={(_val, option) => applyPlaceRates(option)}
                notFoundContent={
                  eventLoading ? "Odds load…" : "Is market pe selection nahi / match dubara choose karo"
                }
              />
            </Form.Item>

            <Space style={{ display: "flex", width: "100%" }} size="middle">
              <Form.Item name="odds" label="Rate" rules={[{ required: true }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: "100%" }} min={0.01} />
              </Form.Item>
              <Form.Item
                name="line"
                label="Runs (fancy only)"
                tooltip="Fancy: run line. BM / MO pe empty."
                style={{ flex: 1 }}>
                <InputNumber style={{ width: "100%" }} min={0} disabled={placeMarketType !== "fancy"} />
              </Form.Item>
              <Form.Item name="stake" label="Stake" rules={[{ required: true }]} style={{ flex: 1 }}>
                <InputNumber style={{ width: "100%" }} min={100} />
              </Form.Item>
            </Space>
            <Button type="primary" block loading={placing} onClick={onPlaceForClient}>
              Accept / Place Bet
            </Button>
          </Form>
        </Card>
      ),
    });
  }

  return (
    <div className="bet-settle-page">
      <div className="bet-settle-head">
        <h2>Bet Settlement</h2>
        {maint ? (
          <Button type="default" onClick={() => setActiveTab("place")} block style={{ marginBottom: 8 }}>
            Accept Bet (Client pe lagaao)
          </Button>
        ) : null}
        <Button type="primary" loading={autoRunning} onClick={onRunAuto} block>
          Run Auto Settle Now
        </Button>
      </div>

      <div className="bet-settle-hint">
        <p>
          <strong>Auto Settle:</strong> Match winner + Fancy session (&quot;X over run TEAM&quot;) — score
          feed se jab overs complete / all-out ho. Cron har minute chalta rahe.
        </p>
        <p>
          Agar koi fancy miss ho jaye to Open pe <em>Settle Fancy</em> se actual runs manually declare
          kar sakte ho.
        </p>
      </div>

      <Tabs
        activeKey={activeTab}
        onChange={setActiveTab}
        className="bet-settle-tabs"
        items={tabItems}
      />

      {maint ? (
        <Modal
          title={`Edit #${editing?.betId || ""}`}
          open={!!editing}
          onCancel={() => setEditing(null)}
          onOk={onSaveEdit}
          confirmLoading={updating}
          destroyOnClose
          width="min(420px, 94vw)">
          <Form form={editForm} layout="vertical">
            <Form.Item name="selection" label="Selection" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="betType" label="Type" rules={[{ required: true }]}>
              <Select
                options={[
                  { value: "back", label: "Back / Yes" },
                  { value: "lay", label: "Lay / No" },
                ]}
              />
            </Form.Item>
            <Form.Item name="marketType" label="Market">
              <Select
                options={[
                  { value: "fancy", label: "Fancy" },
                  { value: "bookmaker", label: "Bookmaker" },
                  { value: "match_odds", label: "Match Odds" },
                ]}
              />
            </Form.Item>
            <Form.Item name="odds" label="Odds / Rate" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={0.01} />
            </Form.Item>
            <Form.Item name="stake" label="Stake" rules={[{ required: true }]}>
              <InputNumber style={{ width: "100%" }} min={1} />
            </Form.Item>
          </Form>
        </Modal>
      ) : null}
    </div>
  );
};

export default BetSettlementAdmin;
